import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import {
  ArrowLeft,
  ClipboardCheck,
  FileText,
  Loader2,
  Pause,
  Play,
  SkipForward,
} from "lucide-react";
import { StudentAppHeader } from "../components/student/StudentAppHeader";
import { STUDENT_COLORS } from "../components/student/StudentMobileShell";
import { InstructionPreviewBody } from "../components/InstructionPreviewBody";
import {
  QuestionnaireDocument,
  type QuestionnaireJsonData,
} from "../components/QuestionnaireDocument";
import {
  buildCaptionTimeline,
  captionIndexAtTime,
  splitSpeechCaptionLines,
} from "../lib/speech-caption";
import {
  STUDENT_TASK_BRIEFING_STORAGE_KEY,
  type StudentTaskBriefingPayload as TaskBriefingPayload,
} from "../lib/student-task-briefing";

interface ScenarioPreviewResponse {
  scenario: {
    id: number;
    summary?: string | null;
    questionnaire?: string | null;
    questionnaireJson?: QuestionnaireJsonData | null;
    targetCategories?: string[];
    targetCategoryLabels?: Record<string, string>;
    guidanceLevel?: string | null;
    briefingScript?: string | null;
    guidanceOptions?: Array<{
      level: string;
      label?: string;
    }>;
    tasks: Array<{
      no?: number;
      title: string;
      details?: string[];
      speech?: string;
    }>;
  };
  avatar: {
    id: number;
    name?: string;
    gender?: string;
    age?: number;
    birthDate?: string | null;
  };
}

interface StartInterviewResponse {
  dentist?: {
    id: number;
    name?: string;
    gender?: string;
  };
  dentistVideoFiles?: {
    baseVideo?: string;
    image?: string;
    dubbingVideo?: string;
  };
}

interface DentistPresentation {
  name: string;
  gender: string;
  baseVideo: string;
  image: string;
  dubbingVideo: string;
}

function selectGuidanceLevel(
  scenario: ScenarioPreviewResponse["scenario"],
): string | undefined {
  if (scenario.guidanceLevel) return scenario.guidanceLevel;
  const options = scenario.guidanceOptions ?? [];

  if (options.length > 1) {
    return (
      options.find((option) => option.level === "viewpoints")?.level ??
      options[Math.floor(options.length / 2)]?.level
    );
  }

  return options[0]?.level ?? scenario.guidanceLevel ?? undefined;
}

const FALLBACK_BRIEFING: TaskBriefingPayload = {
  categoryName: "初診時医療面接",
  patientName: "患者",
  taskTitle: "来院理由の確認から疾患患部の情報聴取",
  taskText:
    "来院理由を確認し、疾患と患部について必要な情報を聴取してください。",
  questionnaireText: null,
};

function loadBriefing(): TaskBriefingPayload {
  try {
    const stored = window.sessionStorage.getItem(STUDENT_TASK_BRIEFING_STORAGE_KEY);
    if (!stored) return FALLBACK_BRIEFING;
    const parsed = JSON.parse(stored) as Partial<TaskBriefingPayload>;
    return {
      instructionId: parsed.instructionId,
      categoryName: parsed.categoryName || FALLBACK_BRIEFING.categoryName,
      patientName: parsed.patientName || FALLBACK_BRIEFING.patientName,
      taskTitle: parsed.taskTitle || FALLBACK_BRIEFING.taskTitle,
      taskText: parsed.taskText || FALLBACK_BRIEFING.taskText,
      questionnaireText: parsed.questionnaireText || null,
      returnPath: parsed.returnPath,
      hideBack: parsed.hideBack,
    };
  } catch {
    return FALLBACK_BRIEFING;
  }
}

export default function StudentTaskBriefing() {
  const [, setLocation] = useLocation();
  const initialBriefing = useMemo(loadBriefing, []);
  const [briefing, setBriefing] = useState(initialBriefing);
  const [scenarioPreview, setScenarioPreview] =
    useState<ScenarioPreviewResponse | null>(null);
  const [dentistPresentation, setDentistPresentation] =
    useState<DentistPresentation | null>(null);
  const [preparationState, setPreparationState] = useState<
    "preparing" | "ready" | "error"
  >("preparing");
  const [preparationError, setPreparationError] = useState("");
  const [documentView, setDocumentView] = useState<
    "task" | "questionnaire" | null
  >(null);
  const [audioState, setAudioState] = useState<
    "loading" | "playing" | "paused" | "ready" | "error"
  >("ready");
  const [hasCompletedBriefing, setHasCompletedBriefing] = useState(false);
  const [activeTaskIndex, setActiveTaskIndex] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const captionFrameRef = useRef<number | null>(null);
  const playbackRunRef = useRef(0);
  const preparationStartedRef = useRef(false);

  const briefingSpeechText = useMemo(() => {
    // 管理画面で作った読み上げ専用の説明文があれば、それ1本だけを話す。
    // 課題文（画面で読む文章）は変えず、音声だけを別の文章へ切り替える。
    const briefingScript = scenarioPreview?.scenario.briefingScript?.trim();
    if (briefingScript) return briefingScript;

    const patientName = briefing.patientName.replace(/さん$/, "");
    const introduction = `それでは、これから歯科衛生士として、${patientName}さんの問診をお願いします。患者さんの言葉をよく聞き、その背景も理解しながら進めてください。今回の問診で確認してほしいことは、次のとおりです。`;
    const closing =
      "以上です。課題文と問診票を確認して、準備ができたら患者面接を始めてください。患者さんとの面接が終わったら、私に報告してください。";
    const tasks = scenarioPreview?.scenario.tasks ?? [];
    if (tasks.length === 0) {
      return [introduction, briefing.taskText, closing].join("");
    }
    return [
      introduction,
      ...tasks.map(
        (task) =>
          task.speech?.trim() ||
          [task.title, ...(task.details ?? [])].filter(Boolean).join("。"),
      ),
      closing,
    ].join("");
  }, [briefing.patientName, briefing.taskText, scenarioPreview]);

  const captionLines = useMemo(
    () => splitSpeechCaptionLines(briefingSpeechText, 20),
    [briefingSpeechText],
  );

  const stopCaptionSync = useCallback(() => {
    if (captionFrameRef.current !== null) {
      window.cancelAnimationFrame(captionFrameRef.current);
      captionFrameRef.current = null;
    }
  }, []);

  const stopAudio = useCallback(() => {
    playbackRunRef.current += 1;
    stopCaptionSync();
    if (audioRef.current) {
      audioRef.current.onpause = null;
      audioRef.current.pause();
    }
    audioRef.current = null;
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    setActiveTaskIndex(null);
  }, [stopCaptionSync]);

  const playBriefing = useCallback(async () => {
    stopAudio();
    const runId = playbackRunRef.current;
    setAudioState("loading");
    try {
      const response = await fetch("/api/generate-audio-sample", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          text: briefingSpeechText,
          gender: dentistPresentation?.gender === "男性" ? "Male" : "Female",
        }),
      });
      if (!response.ok) {
        throw new Error("面接前の説明音声を生成できませんでした");
      }

      const audioUrl = URL.createObjectURL(await response.blob());
      if (runId !== playbackRunRef.current) {
        URL.revokeObjectURL(audioUrl);
        return;
      }
      audioUrlRef.current = audioUrl;
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      await new Promise<void>((resolve, reject) => {
        if (audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
          resolve();
          return;
        }
        audio.onloadedmetadata = () => resolve();
        audio.onerror = () => reject(new Error("音声を読み込めませんでした"));
      });
      if (runId !== playbackRunRef.current) return;

      const captionTimeline = buildCaptionTimeline(
        captionLines,
        audio.duration,
      );
      const syncCaption = () => {
        if (runId !== playbackRunRef.current || audio.ended) return;
        const captionIndex = captionIndexAtTime(
          captionTimeline.startTimes,
          audio.currentTime,
        );
        setActiveTaskIndex(captionIndex >= 0 ? captionIndex : null);
        if (!audio.paused) {
          captionFrameRef.current = window.requestAnimationFrame(syncCaption);
        }
      };

      audio.onplay = () => {
        setAudioState("playing");
        stopCaptionSync();
        syncCaption();
      };
      audio.onpause = () => {
        stopCaptionSync();
        if (!audio.ended) setAudioState("paused");
      };
      audio.onended = () => {
        stopCaptionSync();
        audioRef.current = null;
        setActiveTaskIndex(null);
        setAudioState("ready");
        setHasCompletedBriefing(true);
      };
      audio.onerror = () => {
        stopCaptionSync();
        setAudioState("error");
      };

      // 最初のテロップを少し先に見せてから、1本の音声を止めずに再生する。
      setActiveTaskIndex(captionLines.length > 0 ? 0 : null);
      await new Promise((resolve) => window.setTimeout(resolve, 240));
      if (runId !== playbackRunRef.current) return;
      await audio.play();
    } catch {
      if (runId === playbackRunRef.current) setAudioState("error");
    }
  }, [
    briefingSpeechText,
    captionLines,
    dentistPresentation?.gender,
    stopAudio,
    stopCaptionSync,
  ]);

  const toggleBriefingPlayback = useCallback(async () => {
    const audio = audioRef.current;
    if (audioState === "playing" && audio) {
      audio.pause();
      return;
    }
    if (audioState === "paused" && audio) {
      try {
        await audio.play();
      } catch {
        setAudioState("error");
      }
      return;
    }
    await playBriefing();
  }, [audioState, playBriefing]);

  const prepareInterview = useCallback(async () => {
    stopAudio();
    setHasCompletedBriefing(false);
    setPreparationState("preparing");
    setPreparationError("");
    const params = new URLSearchParams(window.location.search);
    const instructionId =
      Number(params.get("instructionId")) || initialBriefing.instructionId;
    if (!instructionId) {
      setPreparationState("error");
      setPreparationError(
        "シナリオ情報が見つかりません。学習画面から選び直してください。",
      );
      return;
    }

    try {
      const defaultPreviewResponse = await fetch(
        `/api/scenarios/${instructionId}/preview`,
        { credentials: "include" },
      );
      if (!defaultPreviewResponse.ok) {
        throw new Error("課題文を読み込めませんでした");
      }
      const defaultPreview =
        (await defaultPreviewResponse.json()) as ScenarioPreviewResponse;
      const guidanceLevel = selectGuidanceLevel(defaultPreview.scenario);
      let preview = defaultPreview;

      if (
        guidanceLevel &&
        guidanceLevel !== defaultPreview.scenario.guidanceLevel
      ) {
        const selectedPreviewResponse = await fetch(
          `/api/scenarios/${instructionId}/preview?guidanceLevel=${encodeURIComponent(guidanceLevel)}`,
          { credentials: "include" },
        );
        if (!selectedPreviewResponse.ok) {
          throw new Error("課題文を読み込めませんでした");
        }
        preview =
          (await selectedPreviewResponse.json()) as ScenarioPreviewResponse;
      }

      const taskText =
        preview.scenario.tasks
          .map((task) =>
            [task.title, ...(task.details ?? [])].filter(Boolean).join("。"),
          )
          .filter(Boolean)
          .join("。") ||
        preview.scenario.summary ||
        initialBriefing.taskText;
      const preparedBriefing: TaskBriefingPayload = {
        instructionId,
        categoryName: initialBriefing.categoryName,
        patientName: preview.avatar.name || "患者",
        taskTitle:
          preview.scenario.summary ||
          preview.scenario.tasks[0]?.title ||
          initialBriefing.taskTitle,
        taskText,
        questionnaireText: preview.scenario.questionnaire || null,
        returnPath: initialBriefing.returnPath,
        hideBack: initialBriefing.hideBack,
      };
      setBriefing(preparedBriefing);
      setScenarioPreview(preview);
      window.sessionStorage.setItem(
        STUDENT_TASK_BRIEFING_STORAGE_KEY,
        JSON.stringify(preparedBriefing),
      );

      const startResponse = await fetch("/api/start-interview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          instructionId,
          ...(guidanceLevel ? { guidanceLevel } : {}),
          avatarId: preview.avatar.id,
        }),
      });
      if (!startResponse.ok) {
        throw new Error("患者面接を準備できませんでした");
      }
      const startPayload =
        (await startResponse.json()) as StartInterviewResponse;
      const dentistGender = startPayload.dentist?.gender ?? "女性";
      const fallbackProfile =
        dentistGender === "男性" ? "male_default" : "female_default";
      setDentistPresentation({
        name: startPayload.dentist?.name ?? "歯科医師",
        gender: dentistGender,
        baseVideo:
          startPayload.dentistVideoFiles?.baseVideo ??
          `/videos/assets/dentists/${fallbackProfile}/base/idle.mp4`,
        image:
          startPayload.dentistVideoFiles?.image ??
          `/videos/assets/dentists/${fallbackProfile}/base/avatar.jpg`,
        dubbingVideo:
          startPayload.dentistVideoFiles?.dubbingVideo ??
          `/videos/assets/dentists/${fallbackProfile}/dubbing/dubbing.mp4`,
      });
      setPreparationState("ready");
      setAudioState("ready");
    } catch (error) {
      setPreparationState("error");
      setPreparationError(
        error instanceof Error ? error.message : "面接を準備できませんでした",
      );
    }
  }, [initialBriefing, stopAudio]);

  useEffect(() => {
    if (!preparationStartedRef.current) {
      preparationStartedRef.current = true;
      void prepareInterview();
    }
    return stopAudio;
  }, [prepareInterview, stopAudio]);

  const openPatientInterview = () => {
    if (preparationState !== "ready" || !hasCompletedBriefing) return;
    stopAudio();
    setLocation("/interview");
  };

  const skipBriefing = () => {
    if (preparationState !== "ready") return;
    stopAudio();
    setHasCompletedBriefing(true);
    setLocation("/interview");
  };

  const canOpenPatientInterview =
    preparationState === "ready" && hasCompletedBriefing;
  const activeCaptionText =
    activeTaskIndex !== null ? captionLines[activeTaskIndex] : null;

  return (
    <>
    <StudentAppHeader />
    <div
      className="fixed inset-0 top-[68px] overflow-hidden"
      data-task-briefing-layout="report-stage"
      style={{
        background: STUDENT_COLORS.bg,
        color: STUDENT_COLORS.ink,
        display: "flex",
        flexDirection: "column",
        fontFamily:
          '"Hiragino Sans", "Noto Sans JP", "Yu Gothic", "Meiryo", system-ui, sans-serif',
      }}
    >
      <header
        style={{
          flex: "0 0 auto",
          padding: "14px 14px 10px",
          background: STUDENT_COLORS.bg,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div
            style={{
              minHeight: 34,
              padding: "5px 12px",
              borderRadius: 999,
              background: STUDENT_COLORS.ink,
              color: STUDENT_COLORS.surface,
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              fontSize: 11,
              fontWeight: 800,
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ opacity: 0.58 }}>患者面接</span>
            <span aria-hidden="true">›</span>
            <span>課題説明</span>
          </div>
          <button
            type="button"
            disabled={!canOpenPatientInterview}
            onClick={openPatientInterview}
            style={{
              flex: "0 0 auto",
              minHeight: 40,
              padding: "8px 13px",
              border: 0,
              borderRadius: 999,
              background: canOpenPatientInterview
                ? "linear-gradient(135deg, #FF8A3D 0%, #FF6B1A 100%)"
                : "linear-gradient(135deg, #D4D4D8 0%, #A1A1AA 100%)",
              color: STUDENT_COLORS.surface,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              fontFamily: "inherit",
              fontSize: 12,
              fontWeight: 800,
              cursor: canOpenPatientInterview ? "pointer" : "not-allowed",
              boxShadow: canOpenPatientInterview
                ? "0 0 0 4px rgba(255,138,61,0.15), 0 8px 22px rgba(255,107,26,0.45)"
                : "0 2px 6px rgba(0,0,0,0.08)",
              whiteSpace: "nowrap",
            }}
          >
            <Play size={15} fill="currentColor" />
            患者面接へ
          </button>
        </div>
        <div
          style={{
            marginTop: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            color: STUDENT_COLORS.muted,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.15em",
          }}
        >
          <span>STEP 03・課題説明</span>
          {!briefing.hideBack && (
          <button
            type="button"
            onClick={() => {
              stopAudio();
              setLocation(briefing.returnPath || "/mypage/learning");
            }}
            style={{
              flex: "0 0 auto",
              minHeight: 32,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              border: `1px solid ${STUDENT_COLORS.line}`,
              borderRadius: 8,
              background: STUDENT_COLORS.surface,
              color: STUDENT_COLORS.ink,
              fontFamily: "inherit",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0,
              cursor: "pointer",
            }}
          >
            <ArrowLeft size={15} aria-hidden="true" />
            学習一覧へ戻る
          </button>
          )}
        </div>
        <h1
          style={{
            margin: "5px 0 0",
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: -0.3,
            lineHeight: 1.25,
          }}
        >
          歯科医師から課題を確認
        </h1>
        <div
          style={{
            marginTop: 5,
            color: STUDENT_COLORS.muted,
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {briefing.categoryName}・{briefing.patientName}さん
          {dentistPresentation ? `・${dentistPresentation.name}` : ""}
        </div>
      </header>

      <main
        aria-label="課題を説明する歯科医師"
        style={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          margin: "0 14px",
          overflow: "hidden",
          border: `1px solid ${STUDENT_COLORS.line}`,
          borderRadius: 12,
          background: STUDENT_COLORS.ink,
        }}
      >
        {dentistPresentation && (
          <video
            key={audioState === "playing" ? "speaking" : "idle"}
            src={
              audioState === "playing"
                ? dentistPresentation.dubbingVideo
                : dentistPresentation.baseVideo
            }
            poster={dentistPresentation.image}
            aria-label={`${dentistPresentation.name}の歯科医師アバター`}
            autoPlay
            loop
            muted
            playsInline
            style={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center center",
              background: STUDENT_COLORS.ink,
              transform: "scale(1.12)",
              transformOrigin: "50% 46%",
            }}
          />
        )}

        {preparationState === "preparing" && (
          <div
            style={{
              position: "absolute",
              left: 12,
              bottom: 12,
              padding: "9px 12px",
              borderRadius: 999,
              background: "rgba(255,255,255,0.92)",
              color: STUDENT_COLORS.cyan,
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            <Loader2 className="animate-spin" size={16} />
            課題文と患者面接を準備中
          </div>
        )}

        {activeCaptionText && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: "absolute",
              left: 4,
              right: 4,
              bottom: 12,
              minHeight: 52,
              padding: "4px 2px",
              color: STUDENT_COLORS.surface,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily:
                '"Hiragino Sans", "Noto Sans JP", "Yu Gothic", "Meiryo", system-ui, sans-serif',
              fontSize: "clamp(12px, calc((100vw - 44px) / 20), 18px)",
              fontWeight: 800,
              lineHeight: 1.4,
              letterSpacing: 0,
              textAlign: "center",
              whiteSpace: "nowrap",
              WebkitTextStroke: `2.5px ${STUDENT_COLORS.ink}`,
              paintOrder: "stroke fill",
              textShadow: `-2px -2px 0 ${STUDENT_COLORS.ink}, 0 -2px 0 ${STUDENT_COLORS.ink}, 2px -2px 0 ${STUDENT_COLORS.ink}, -2px 0 0 ${STUDENT_COLORS.ink}, 2px 0 0 ${STUDENT_COLORS.ink}, -2px 2px 0 ${STUDENT_COLORS.ink}, 0 2px 0 ${STUDENT_COLORS.ink}, 2px 2px 0 ${STUDENT_COLORS.ink}`,
              overflow: "hidden",
            }}
          >
            {activeCaptionText}
          </div>
        )}

        {preparationState === "ready" && !hasCompletedBriefing && (
          <button
            type="button"
            onClick={skipBriefing}
            aria-label="歯科医師からの説明をスキップして患者面接へ進む"
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              padding: "6px 12px",
              border: `1px solid ${STUDENT_COLORS.line}`,
              borderRadius: 8,
              background: STUDENT_COLORS.surface,
              color: STUDENT_COLORS.ink,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "inherit",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            <SkipForward size={15} />
            説明をスキップ
          </button>
        )}

        {preparationState === "error" && (
          <div
            style={{
              position: "absolute",
              left: 12,
              right: 12,
              bottom: 12,
              padding: 12,
              borderRadius: 10,
              background: STUDENT_COLORS.roseSoft,
              color: STUDENT_COLORS.rose,
              fontSize: 12,
              fontWeight: 800,
              lineHeight: 1.6,
            }}
          >
            <div>{preparationError}</div>
            <button
              type="button"
              onClick={() => void prepareInterview()}
              style={{
                marginTop: 8,
                padding: "7px 10px",
                border: `1px solid ${STUDENT_COLORS.rose}`,
                borderRadius: 8,
                background: STUDENT_COLORS.surface,
                color: STUDENT_COLORS.rose,
                fontFamily: "inherit",
                fontSize: 12,
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              準備を再試行
            </button>
          </div>
        )}
      </main>

      <footer
        style={{
          flex: "0 0 auto",
          padding: "10px 14px max(14px, env(safe-area-inset-bottom))",
          borderTop: `1px solid ${STUDENT_COLORS.line}`,
          background: STUDENT_COLORS.surface,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            gap: 12,
          }}
        >
          <button
            type="button"
            onClick={() => setDocumentView("task")}
            style={documentButtonStyle(false)}
          >
            <ClipboardCheck size={22} />
            課題文
          </button>
          <button
            type="button"
            disabled={preparationState !== "ready" || audioState === "loading"}
            onClick={() => void toggleBriefingPlayback()}
            aria-label={audioState === "playing" ? "一時停止" : "再生"}
            style={{
              width: 88,
              height: 88,
              justifySelf: "center",
              border: `3px solid ${STUDENT_COLORS.surface}`,
              borderRadius: 44,
              background:
                audioState === "playing"
                  ? "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
                  : "linear-gradient(135deg, #20B8A8 0%, #0E9384 100%)",
              color: STUDENT_COLORS.surface,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              fontFamily: "inherit",
              fontSize: 9.5,
              fontWeight: 800,
              cursor:
                preparationState === "ready" && audioState !== "loading"
                  ? "pointer"
                  : "not-allowed",
              opacity:
                preparationState === "ready" && audioState !== "loading"
                  ? 1
                  : 0.55,
              boxShadow:
                audioState === "playing"
                  ? "0 0 0 8px rgba(245,158,11,0.18), 0 14px 36px rgba(217,119,6,0.45)"
                  : "0 0 0 8px rgba(32,184,168,0.18), 0 14px 36px rgba(14,147,132,0.45)",
            }}
          >
            {audioState === "loading" ? (
              <Loader2 className="animate-spin" size={24} />
            ) : audioState === "playing" ? (
              <Pause size={24} fill="currentColor" />
            ) : (
              <Play size={24} fill="currentColor" />
            )}
            {audioState === "loading"
              ? "準備中"
              : audioState === "playing"
                ? "一時停止"
                : "再生"}
          </button>
          <button
            type="button"
            onClick={() => setDocumentView("questionnaire")}
            style={documentButtonStyle(false)}
          >
            <FileText size={22} />
            問診票
          </button>
        </div>
        {audioState === "error" && (
          <div
            role="alert"
            style={{
              marginTop: 10,
              color: STUDENT_COLORS.muted,
              textAlign: "center",
              fontSize: 11,
            }}
          >
            音声を準備できませんでした。「再生」をもう一度押してください。
          </div>
        )}
      </footer>

      {documentView && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 30,
            background: STUDENT_COLORS.bg,
            color: STUDENT_COLORS.ink,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              flex: "0 0 auto",
              padding: "12px 14px",
              borderBottom: `1px solid ${STUDENT_COLORS.line}`,
              background: STUDENT_COLORS.surface,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div>
                <div
                  style={{
                    color: STUDENT_COLORS.muted,
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: "0.15em",
                  }}
                >
                  STEP 03・課題説明
                </div>
                <div style={{ marginTop: 4, fontSize: 18, fontWeight: 800 }}>
                  {documentView === "task" ? "課題文" : "問診票"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDocumentView(null)}
                aria-label="課題説明へ戻る"
                style={{
                  width: 38,
                  height: 38,
                  border: `1px solid ${STUDENT_COLORS.line}`,
                  borderRadius: 999,
                  background: STUDENT_COLORS.surface,
                  color: STUDENT_COLORS.ink,
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                }}
              >
                <ArrowLeft size={19} />
              </button>
            </div>
            <div
              style={{
                marginTop: 12,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 6,
                padding: 4,
                border: `1px solid ${STUDENT_COLORS.line}`,
                borderRadius: 999,
                background: STUDENT_COLORS.bg,
              }}
            >
              <button
                type="button"
                onClick={() => setDocumentView("task")}
                style={documentTabStyle(documentView === "task")}
              >
                課題文
              </button>
              <button
                type="button"
                onClick={() => setDocumentView("questionnaire")}
                style={documentTabStyle(documentView === "questionnaire")}
              >
                問診票
              </button>
            </div>
          </div>

          <div
            aria-live="polite"
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              padding: "14px 14px 18px",
            }}
          >
            {documentView === "task" ? (
              <InstructionPreviewBody
                summary={
                  scenarioPreview?.scenario.summary ?? briefing.taskTitle
                }
                targetCategories={
                  scenarioPreview?.scenario.targetCategories ?? []
                }
                categoryLabels={scenarioPreview?.scenario.targetCategoryLabels}
                tasks={
                  scenarioPreview?.scenario.tasks ?? [
                    { no: 1, title: briefing.taskText },
                  ]
                }
                maxWidth={720}
              />
            ) : scenarioPreview?.scenario.questionnaireJson ? (
              <QuestionnaireDocument
                json={scenarioPreview.scenario.questionnaireJson}
                patientName={scenarioPreview.avatar.name}
                meta={{
                  age: scenarioPreview.avatar.age,
                  gender: scenarioPreview.avatar.gender,
                  birthDate: scenarioPreview.avatar.birthDate,
                }}
                compact
              />
            ) : (
              <LegacyQuestionnaireText
                text={
                  briefing.questionnaireText || "問診票が未設定です。問診票を生成してください。"
                }
                patientName={briefing.patientName}
              />
            )}
          </div>

          <div
            style={{
              flex: "0 0 auto",
              padding: "10px 14px max(14px, env(safe-area-inset-bottom))",
              borderTop: `1px solid ${STUDENT_COLORS.line}`,
              background: STUDENT_COLORS.surface,
            }}
          >
            <button
              type="button"
              onClick={() => setDocumentView(null)}
              style={{
                width: "100%",
                minHeight: 44,
                border: `1px solid ${STUDENT_COLORS.line}`,
                borderRadius: 8,
                background: STUDENT_COLORS.surface,
                color: STUDENT_COLORS.ink,
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              課題説明に戻る
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

function LegacyQuestionnaireText({
  text,
  patientName,
}: {
  text: string;
  patientName: string;
}) {
  return (
    <div
      style={{
        padding: "22px 16px 26px",
        border: `1px solid ${STUDENT_COLORS.paperLine}`,
        borderRadius: 12,
        background: STUDENT_COLORS.paper,
        color: STUDENT_COLORS.ink,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 12,
          paddingBottom: 14,
          marginBottom: 14,
          borderBottom: `2px solid ${STUDENT_COLORS.paperLine}`,
        }}
      >
        <div>
          <div
            style={{
              color: STUDENT_COLORS.muted,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.15em",
            }}
          >
            MEDICAL INTERVIEW SHEET
          </div>
          <div style={{ marginTop: 4, fontSize: 20, fontWeight: 800 }}>
            問 診 票
          </div>
        </div>
        <div style={{ color: STUDENT_COLORS.muted, fontSize: 12 }}>
          {patientName}
        </div>
      </div>
      <pre
        style={{
          margin: 0,
          color: STUDENT_COLORS.ink,
          fontFamily: "inherit",
          fontSize: 12.5,
          lineHeight: 1.75,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
      >
        {text}
      </pre>
    </div>
  );
}

function documentButtonStyle(_selected: boolean) {
  return {
    height: 78,
    width: "100%",
    border: `1px solid ${STUDENT_COLORS.line}`,
    borderRadius: 14,
    background: STUDENT_COLORS.surface,
    color: STUDENT_COLORS.ink,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontFamily: "inherit",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  };
}

function documentTabStyle(selected: boolean) {
  return {
    minHeight: 38,
    border: 0,
    borderRadius: 999,
    background: selected ? STUDENT_COLORS.surface : "transparent",
    color: selected ? STUDENT_COLORS.ink : STUDENT_COLORS.muted,
    fontFamily: "inherit",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  };
}
